import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import PlayerHeadshot from "../PlayerHeadshot";

const GreatsFeaturedPlayer = ({ player, featured = false, onSelect, sport = "mlb" }) => {
  const size = featured ? 112 : 72;
  return (
    <Box
      onClick={() => player.playerId && onSelect?.(player.playerId)}
      sx={{
        cursor: player.playerId ? "pointer" : "default",
        minWidth: 0,
      }}
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ letterSpacing: "0.14em", display: "block", mb: 1 }}
      >
        {player.rank}
      </Typography>
      <PlayerHeadshot
        playerId={player.playerId}
        sport={sport}
        src={player.photoUrl}
        size={size}
        showTooltip={false}
        showLoading={false}
        enabled={Boolean(player.playerId) && player.photoUrl === undefined}
        loading={featured ? "eager" : "lazy"}
        fetchPriority={featured ? "high" : undefined}
      />
      <Typography
        variant={featured ? "h5" : "h6"}
        fontWeight={700}
        sx={{ mt: 1.5, lineHeight: 1.2 }}
        noWrap
        title={player.playerName}
      >
        {player.playerName}
      </Typography>
      <Typography
        variant={featured ? "h4" : "h5"}
        fontWeight={800}
        sx={{ mt: 0.5, fontVariantNumeric: "tabular-nums" }}
      >
        {player.displayValue}
      </Typography>
    </Box>
  );
};

const GreatsFeatured = ({ players = [], loading = false, onSelect, sport = "mlb" }) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr 1fr", md: "1.2fr 1fr 1fr" },
          gridTemplateAreas: {
            xs: '"one one" "two three"',
            md: '"one two three"',
          },
          mb: 4,
        }}
      >
        {[0, 1, 2].map((index) => (
          <Box
            key={index}
            sx={{ gridArea: ["one", "two", "three"][index] }}
          >
            <Skeleton
              variant="circular"
              width={index === 0 ? 112 : 72}
              height={index === 0 ? 112 : 72}
            />
            <Skeleton width="70%" sx={{ mt: 2 }} />
            <Skeleton width="40%" />
          </Box>
        ))}
      </Box>
    );
  }

  if (!players.length) return null;

  return (
    <Box
      sx={{
        display: "grid",
        gap: { xs: 3, md: 4 },
        gridTemplateColumns: { xs: "1fr 1fr", md: "1.2fr 1fr 1fr" },
        gridTemplateAreas: {
          xs: '"one one" "two three"',
          md: '"one two three"',
        },
        alignItems: "end",
        mb: 4,
        pb: 4,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      {players.map((player, index) => (
        <Box
          key={player.id || player.playerId || player.rank}
          sx={{ gridArea: ["one", "two", "three"][index] }}
        >
          <GreatsFeaturedPlayer
            player={player}
            featured={index === 0}
            sport={sport}
            onSelect={onSelect}
          />
        </Box>
      ))}
    </Box>
  );
};

export default GreatsFeatured;
